/* ==========================================
   ⚠️ CUSTOM ASSET: Shared Delivery Rules
   Last Modified: 2026-10-15
   Author: Chad Walker (via Claude Code)
   ==========================================

   ICM: WF-DEC-0008 (one rules owner), WF-DEC-0002 (lead-time resolution),
     WF-DEC-0003 (cart owns validity), WF-FEA-0002 (delivery system),
     WF-RES-0005 (platform facts). Read those records before changing a rule.

   Purpose:
     The ONE implementation of Woodland's delivery business rules, used by
     both snippets/wf-pdp-date-picker.liquid and snippets/wf-cart-date-grid.
     liquid. Pure functions only: no Liquid, no DOM, no fetch, no cart
     writes. Each picker keeps its own UI and owns its own writes.

   Rules (WF-DEC-0002):
     - Per line: product-type override if one applies, else the site
       default. The default is a fallback, never a floor.
     - Per cart: max over lines with requires_shipping === true (physical
       nested add-ons are ordinary lines and count). ASAP min and max each
       take the max independently.
     - Type match trims whitespace and ignores case. Garland != Garlands.
     - Business days skip Saturdays, Sundays and carrier holidays. Holidays
       can't be arrival dates; weekends can.
     - Ordinary dates must fall within schedule_window_days. A committed
       event (label AND date equal the configured preorder) is exempt from
       that cap but still obeys lead time and holidays.
     - Christmas preorder (WF-DEC-0015): a cart holding any line tagged
       `christmas preorder` may only commit a Christmas arrival date: inside
       the configured arrival window AND passing lead time and holidays
       (window cap exempt). Identity is the fixed delivery_event
       CHRISTMAS_EVENT, never the merchant's display label. A Christmas
       commitment in a cart with no tagged line is invalid (re-choose).

   Dates are ISO "YYYY-MM-DD" strings. Day arithmetic runs on UTC Date
   objects, so local DST changes can't shift a day. "Today" is the store's
   date, from the store UTC offset Liquid renders ({{ 'now' | date: '%z' }}),
   not the browser's local date (WF-DEC-0008).

   Usage:
     Loaded (defer) by snippets/wf-delivery-rules-data.liquid, which also
     renders the JSON config payload:
       const cfg = WFDeliveryRules.createConfig(JSON.parse(payloadText));
       const rules = WFDeliveryRules.resolveCart(cfg, cart.items);
   ==========================================
*/

(function (root) {
  'use strict';

  if (root.WFDeliveryRules) return; // idempotent: safe if the tag renders twice

  var ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

  // Fallbacks used only when a site-wide setting is empty (matches the
  // settings_schema.json defaults).
  var FALLBACK = { scheduled: 8, asapMin: 2, asapMax: 3, windowDays: 90 };

  // Christmas preorder identity (WF-DEC-0015). Written to delivery_event and
  // read by fulfilment; independent of the editable display label, so a
  // label change never orphans a saved cart. Never change this value.
  var CHRISTMAS_EVENT = 'Christmas Preorder';
  var CHRISTMAS_DEFAULT_LABEL = 'Christmas Preorder';

  // ─── ISO date helpers ──────────────────────────────────────────────────────
  function parseISO(iso) {
    if (typeof iso !== 'string') return null;
    var m = ISO_RE.exec(iso.trim());
    if (!m) return null;
    var y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
    var date = new Date(Date.UTC(y, mo, d));
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo || date.getUTCDate() !== d) return null;
    return date;
  }

  function toISO(date) {
    var y = date.getUTCFullYear();
    var m = String(date.getUTCMonth() + 1).padStart(2, '0');
    var d = String(date.getUTCDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  function isValidISO(iso) { return parseISO(iso) !== null; }

  function isoFromParts(year, monthIndex, day) {
    return toISO(new Date(Date.UTC(year, monthIndex, day)));
  }

  function partsOf(iso) {
    var d = parseISO(iso);
    return d ? { year: d.getUTCFullYear(), month: d.getUTCMonth(), day: d.getUTCDate() } : null;
  }

  function addDays(iso, n) {
    var d = parseISO(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return toISO(d);
  }

  function dayOfWeek(iso) { return parseISO(iso).getUTCDay(); }

  // "October 7": the format Ship by and every picker label already use.
  function formatShort(iso) {
    var p = partsOf(iso);
    return p ? MONTH_NAMES[p.month] + ' ' + p.day : '';
  }

  // "from December 10 to December 20", or "on December 10" for one day.
  // Years are added to both ends when the range crosses a year or is not in
  // today's year ("from December 28, 2026 to January 4, 2027"). Display
  // only (WF-DEC-0015).
  function formatRange(first, last, today) {
    var a = partsOf(first), b = partsOf(last), t = partsOf(today);
    if (!a || !b) return '';
    var years = a.year !== b.year || (t && a.year !== t.year);
    var one = function (iso, p) { return formatShort(iso) + (years ? ', ' + p.year : ''); };
    return first === last ? 'on ' + one(first, a) : 'from ' + one(first, a) + ' to ' + one(last, b);
  }

  // ─── Config parsing ────────────────────────────────────────────────────────
  function positiveInt(value) {
    var n = Number(value);
    if (value === null || value === undefined || value === '' || !isFinite(n) || n <= 0) return null;
    return Math.floor(n);
  }

  function normType(type) {
    return String(type == null ? '' : type).trim().toLowerCase();
  }

  // One ISO date per line; blank lines trimmed; anything that isn't a real
  // YYYY-MM-DD date is ignored (and reported), never thrown.
  function parseHolidays(text) {
    var valid = [], ignored = [];
    String(text == null ? '' : text).split(/\r?\n/).forEach(function (line) {
      var entry = line.trim();
      if (!entry) return;
      if (isValidISO(entry)) {
        if (valid.indexOf(entry) === -1) valid.push(entry);
      } else {
        ignored.push(entry);
      }
    });
    return { valid: valid.sort(), ignored: ignored };
  }

  // "-0700" / "+0530" / "-07:00" → minutes east of UTC, or null.
  function parseOffset(offset) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(offset == null ? '' : offset).trim());
    if (!m) return null;
    var minutes = Number(m[2]) * 60 + Number(m[3]);
    return m[1] === '-' ? -minutes : minutes;
  }

  // The store's calendar date right now: client clock + store offset.
  // Robust to cached HTML because only the offset comes from the render.
  function storeTodayISO(offsetMinutes, nowMs) {
    return toISO(new Date(nowMs + offsetMinutes * 60000));
  }

  function browserTodayISO(nowMs) {
    var d = new Date(nowMs);
    return isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // raw = the JSON payload from snippets/wf-delivery-rules-data.liquid.
  function createConfig(raw, nowMs) {
    raw = raw || {};
    var now = typeof nowMs === 'number' ? nowMs : Date.now();
    var def = raw.defaults || {};

    var defaults = {
      scheduled: positiveInt(def.scheduled) || FALLBACK.scheduled,
      asapMin:   positiveInt(def.asapMin)   || FALLBACK.asapMin,
      asapMax:   positiveInt(def.asapMax)   || FALLBACK.asapMax
    };
    if (defaults.asapMax < defaults.asapMin) defaults.asapMax = defaults.asapMin;

    // Slots in order; a blank type skips the slot; a duplicate type means
    // the later slot wins; a blank/0 field falls back to the default.
    var overrides = {};
    (raw.overrides || []).forEach(function (slot) {
      if (!slot) return;
      var key = normType(slot.type);
      if (!key) return;
      overrides[key] = {
        scheduled: positiveInt(slot.scheduled),
        asapMin:   positiveInt(slot.asapMin),
        asapMax:   positiveInt(slot.asapMax)
      };
    });

    var holidays = parseHolidays(raw.holidays);

    var event = null;
    if (raw.event) {
      var label = String(raw.event.label == null ? '' : raw.event.label).trim();
      var date = String(raw.event.date == null ? '' : raw.event.date).trim();
      if (label && isValidISO(date)) event = { label: label, date: date };
    }

    // Christmas arrival window (WF-DEC-0015). state: 'ok' | 'disabled' |
    // 'malformed'. Start/end are customer ARRIVAL dates, not ship dates.
    var xm = raw.christmas || {};
    var xStart = String(xm.start == null ? '' : xm.start).trim();
    var xEnd = String(xm.end == null ? '' : xm.end).trim();
    var xState = 'disabled';
    if (xm.enabled === true) {
      xState = isValidISO(xStart) && isValidISO(xEnd) && xStart <= xEnd ? 'ok' : 'malformed';
      if (xState === 'malformed' && typeof console !== 'undefined') console.warn('WFDeliveryRules: Christmas preorder window missing, malformed or reversed', xm.start, xm.end);
    }
    var christmas = {
      state: xState,
      label: String(xm.label == null ? '' : xm.label).trim() || CHRISTMAS_DEFAULT_LABEL,
      message: String(xm.message == null ? '' : xm.message).trim(),
      start: xState === 'ok' ? xStart : null,
      end: xState === 'ok' ? xEnd : null
    };

    var offsetMinutes = parseOffset(raw.storeUtcOffset);
    var today;
    if (offsetMinutes === null) {
      // A malformed payload must not break the picker; report it loudly.
      if (typeof console !== 'undefined') console.warn('WFDeliveryRules: store UTC offset missing or malformed; using browser date', raw.storeUtcOffset);
      today = browserTodayISO(now);
    } else {
      today = storeTodayISO(offsetMinutes, now);
    }

    return {
      today: today,
      todaySource: offsetMinutes === null ? 'browser' : 'store',
      storeUtcOffset: raw.storeUtcOffset,
      offsetMinutes: offsetMinutes,
      defaults: defaults,
      overrides: overrides,
      windowDays: positiveInt(raw.windowDays) || FALLBACK.windowDays,
      holidays: holidays.valid,
      ignoredHolidays: holidays.ignored,
      event: event,
      // Thanksgiving pill eligibility inputs: the same rule the cart Liquid
      // applied at render time, now re-evaluable on the same page.
      eventConfigured: !!raw.event,
      eventRestricted: raw.eventRestricted === true,
      christmas: christmas
    };
  }

  // ─── Lead-time resolution (WF-DEC-0002) ────────────────────────────────────
  // line: an Ajax Cart line item, or the same shape from Liquid
  // ({ product_type, requires_shipping }).
  function participates(line) {
    return !!line && line.requires_shipping === true;
  }

  function resolveLine(cfg, line) {
    var ov = cfg.overrides[normType(line && line.product_type)] || {};
    var d = cfg.defaults;
    var asapMin = ov.asapMin != null ? ov.asapMin : d.asapMin;
    var asapMax = ov.asapMax != null ? ov.asapMax : (ov.asapMin != null ? ov.asapMin : d.asapMax);
    if (asapMax < asapMin) asapMax = asapMin;
    return {
      scheduled: ov.scheduled != null ? ov.scheduled : d.scheduled,
      asapMin: asapMin,
      asapMax: asapMax
    };
  }

  // null when nothing in the cart ships: no delivery rules apply.
  function resolveCart(cfg, lines) {
    var result = null;
    (lines || []).forEach(function (line) {
      if (!participates(line)) return;
      var r = resolveLine(cfg, line);
      if (!result) {
        result = { scheduled: r.scheduled, asapMin: r.asapMin, asapMax: r.asapMax };
      } else {
        result.scheduled = Math.max(result.scheduled, r.scheduled);
        result.asapMin   = Math.max(result.asapMin, r.asapMin);
        result.asapMax   = Math.max(result.asapMax, r.asapMax);
      }
    });
    return result;
  }

  // Display-only rules when nothing ships (e.g. a gift-card-only cart).
  function defaultRules(cfg) {
    return { scheduled: cfg.defaults.scheduled, asapMin: cfg.defaults.asapMin, asapMax: cfg.defaults.asapMax };
  }

  // ─── Calendar math ─────────────────────────────────────────────────────────
  function isHoliday(cfg, iso) { return cfg.holidays.indexOf(iso) !== -1; }

  function addBusinessDays(cfg, iso, days) {
    var d = iso;
    var remaining = Math.abs(days);
    var step = days >= 0 ? 1 : -1;
    while (remaining > 0) {
      d = addDays(d, step);
      var dow = dayOfWeek(d);
      if (dow !== 0 && dow !== 6 && !isHoliday(cfg, d)) remaining--;
    }
    return d;
  }

  function earliestScheduled(cfg, rules) { return addBusinessDays(cfg, cfg.today, rules.scheduled); }
  function latestScheduled(cfg) { return addDays(cfg.today, cfg.windowDays); }

  function asapRange(cfg, rules) {
    return {
      start: addBusinessDays(cfg, cfg.today, rules.asapMin),
      end:   addBusinessDays(cfg, cfg.today, rules.asapMax)
    };
  }

  // The derived Ship by attribute (ASAP only), e.g. "October 7–October 9".
  function shipBy(cfg, rules) {
    var r = asapRange(cfg, rules);
    return formatShort(r.start) + '–' + formatShort(r.end);
  }

  // Ships-pill window for a chosen arrival date.
  function shipsWindow(cfg, arrivalIso) {
    return {
      start: addBusinessDays(cfg, arrivalIso, -3),
      end:   addBusinessDays(cfg, arrivalIso, -2)
    };
  }

  // ─── Validity ──────────────────────────────────────────────────────────────
  // → 'valid' | 'invalid_date' | 'holiday' | 'too_early' | 'too_late'
  function validateDate(cfg, rules, iso, opts) {
    if (!isValidISO(iso)) return 'invalid_date';
    if (isHoliday(cfg, iso)) return 'holiday';
    if (iso < earliestScheduled(cfg, rules)) return 'too_early';
    if (!(opts && opts.event) && iso > latestScheduled(cfg)) return 'too_late';
    return 'valid';
  }

  // A committed date is the configured event only while BOTH the label and
  // the date still match the current preorder settings.
  function isEvent(cfg, attrs) {
    attrs = attrs || {};
    return !!cfg.event &&
      attrs.delivery_method === 'Scheduled' &&
      attrs.delivery_event === cfg.event.label &&
      attrs.delivery_date === cfg.event.date;
  }

  // Validity of the configured event for these rules (window exempt).
  function eventStatus(cfg, rules) {
    if (!cfg.event) return 'invalid_date';
    return validateDate(cfg, rules, cfg.event.date, { event: true });
  }

  // Whether the Thanksgiving (configured event) pill is offered for these
  // lines: enabled, and either unrestricted or some line is in the
  // configured collection (line.thanksgiving, from Liquid). Unchanged rule;
  // Christmas carts hide the pill separately.
  function eventOffered(cfg, lines) {
    if (!cfg.eventConfigured) return false;
    if (!cfg.eventRestricted) return true;
    return (lines || []).some(function (line) { return !!line && line.thanksgiving === true; });
  }

  // ─── Christmas preorder (WF-DEC-0015) ──────────────────────────────────────
  // Any line tagged `christmas preorder` (line.christmas, from Liquid) makes
  // the whole cart Christmas-only.
  function christmasCart(lines) {
    return (lines || []).some(function (line) { return !!line && line.christmas === true; });
  }

  // A saved Christmas commitment: Scheduled + the fixed identity. The legacy
  // Thanksgiving identity wins if a merchant ever gives it the same text.
  function isChristmas(cfg, attrs) {
    attrs = attrs || {};
    return attrs.delivery_method === 'Scheduled' &&
      attrs.delivery_event === CHRISTMAS_EVENT &&
      !isEvent(cfg, attrs);
  }

  // → 'valid' | 'invalid_date' | 'unavailable' | 'outside_window' |
  //   'holiday' | 'too_early'. Window bounds are inclusive arrival dates;
  //   lead time is the resolved scheduled lead of the cart (or product).
  function validateChristmasDate(cfg, rules, iso) {
    if (!isValidISO(iso)) return 'invalid_date';
    var x = cfg.christmas;
    if (!x || x.state !== 'ok') return 'unavailable';
    if (iso < x.start || iso > x.end) return 'outside_window';
    if (isHoliday(cfg, iso)) return 'holiday';
    if (iso < earliestScheduled(cfg, rules)) return 'too_early';
    return 'valid';
  }

  // First and last selectable Christmas arrival dates for these rules, or
  // null when the window is disabled/malformed or nothing is reachable.
  function christmasDates(cfg, rules) {
    var x = cfg.christmas;
    if (!x || x.state !== 'ok' || !rules) return null;
    var first = null, last = null;
    for (var d = x.start, n = 0; d <= x.end && n < 800; d = addDays(d, 1), n++) {
      if (validateChristmasDate(cfg, rules, d) === 'valid') {
        if (!first) first = d;
        last = d;
      }
    }
    return first ? { first: first, last: last } : null;
  }

  // Whole-cart status of the committed attributes (WF-DEC-0003 r5/r6):
  // 'not_applicable' (nothing ships) | 'unset' (no method) | 'valid' |
  // 'too_early' | 'holiday' | 'too_late' | 'invalid_date' | 'unrecognized'.
  // Christmas (WF-DEC-0015): 'christmas_not_applicable' = Christmas saved
  // but no tagged line. With ctx.christmasCart: 'christmas_unavailable' (no
  // reachable date) | 'christmas_unset' | 'christmas_required' (another
  // method saved) | 'christmas_' + a validateChristmasDate failure | 'valid'.
  // Never derived from Ship by.
  function committedStatus(cfg, rules, attrs, ctx) {
    attrs = attrs || {};
    if (!rules) return 'not_applicable';
    var method = attrs.delivery_method || '';
    if (ctx && ctx.christmasCart) {
      if (!christmasDates(cfg, rules)) return 'christmas_unavailable';
      if (!method) return 'christmas_unset';
      if (!isChristmas(cfg, attrs)) return 'christmas_required';
      var xs = validateChristmasDate(cfg, rules, attrs.delivery_date || '');
      return xs === 'valid' ? 'valid' : 'christmas_' + xs;
    }
    if (isChristmas(cfg, attrs)) return 'christmas_not_applicable';
    if (!method) return 'unset';
    if (method === 'ASAP') return 'valid';
    if (method !== 'Scheduled') return 'unrecognized';
    return validateDate(cfg, rules, attrs.delivery_date || '', { event: isEvent(cfg, attrs) });
  }

  // ─── Debug (read-only; used by Chad's acceptance console snippets) ─────────
  var debugProvider = null;
  function registerDebug(fn) { debugProvider = fn; }
  function debugSummary() { return debugProvider ? debugProvider() : null; }

  root.WFDeliveryRules = {
    MONTH_NAMES: MONTH_NAMES,
    CHRISTMAS_EVENT: CHRISTMAS_EVENT,
    parseISO: parseISO,
    toISO: toISO,
    isValidISO: isValidISO,
    isoFromParts: isoFromParts,
    partsOf: partsOf,
    addDays: addDays,
    formatShort: formatShort,
    formatRange: formatRange,
    normType: normType,
    parseHolidays: parseHolidays,
    parseOffset: parseOffset,
    storeTodayISO: storeTodayISO,
    createConfig: createConfig,
    participates: participates,
    resolveLine: resolveLine,
    resolveCart: resolveCart,
    defaultRules: defaultRules,
    isHoliday: isHoliday,
    addBusinessDays: addBusinessDays,
    earliestScheduled: earliestScheduled,
    latestScheduled: latestScheduled,
    asapRange: asapRange,
    shipBy: shipBy,
    shipsWindow: shipsWindow,
    validateDate: validateDate,
    isEvent: isEvent,
    eventStatus: eventStatus,
    committedStatus: committedStatus,
    eventOffered: eventOffered,
    christmasCart: christmasCart,
    isChristmas: isChristmas,
    validateChristmasDate: validateChristmasDate,
    christmasDates: christmasDates,
    registerDebug: registerDebug,
    debugSummary: debugSummary
  };
})(typeof window !== 'undefined' ? window : globalThis);
